import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap, CalendarClock, Video, Loader2, Upload, Link2, Trash2, FileText,
  Image as ImageIcon, Music, Presentation, LogOut, Paperclip, ClipboardList, CheckCircle2,
  AlertCircle, BarChart3, Star, Trophy, ArrowLeft, CalendarDays, Radio, FolderOpen,
  MessageSquare, FileSignature, Wallet, MessagesSquare, ExternalLink, Send, Bot, Users,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { instructorApi, connectApi, apiError, logout, mediaUrl } from "../lib/api";

const TIPO_ICON = { imagen: ImageIcon, audio: Music, video: Video, presentacion: Presentation, documento: FileText, archivo: Paperclip };
const today = () => new Date().toISOString().slice(0, 10);
const fmtSemana = (iso) => { try { const d = new Date(iso + "T00:00:00"); const e = new Date(d); e.setDate(e.getDate() + 6); const o = { day: "numeric", month: "short" }; return `${d.toLocaleDateString("es-MX", o)} – ${e.toLocaleDateString("es-MX", o)}`; } catch { return iso; } };

const MENU = [
  ["clases", "Mis Clases Asignadas", CalendarDays, "#E9A400"],
  ["superaconnect", "Supera Connect (En vivo)", Radio, "#C0140F"],
  ["conectar", "Conectarme a mi Clase", Radio, "#2F5BEA"],
  ["grabadas", "Mis Clases Grabadas", Video, "#8E44AD"],
  ["calificacion", "Mi Calificación", Star, "#22A860"],
  ["evaluacion", "Evaluación a Candidatos", ClipboardList, "#7B3FBF"],
  ["ranking", "Ranking General", Trophy, "#E9A400"],
  ["materiales", "Materiales", FolderOpen, "#0A9BAF"],
  ["preguntas", "Preguntas y Comentarios", MessagesSquare, "#C0392B"],
  ["contratacion", "Contratación", FileSignature, "#2A3446"],
  ["pagos", "Mis Pagos Recibidos", Wallet, "#22A860"],
  ["chat", "Chat", MessageSquare, "#2F5BEA"],
];

const Section = ({ title, color, icon: Ic, children }) => (
  <div>
    <h2 className="font-extrabold text-[#0A1C30] text-lg flex items-center gap-2"><span className="w-1.5 h-5 rounded-full" style={{ background: color }} /> {Ic && <Ic className="w-5 h-5" style={{ color }} />} {title}</h2>
    <div className="mt-4">{children}</div>
  </div>
);
const Empty = ({ children }) => <div className="bg-white rounded-2xl ring-1 ring-black/5 p-8 text-center text-[#6B7688] font-semibold">{children}</div>;

/* ---------------- Clases asignadas ---------------- */
function ClasesView({ clases }) {
  const t = today();
  return (
    <Section title="Mis Clases Asignadas" color="#E9A400" icon={CalendarDays}>
      {clases.length === 0 ? <Empty>Aún no tienes clases asignadas. El administrador te las asignará pronto.</Empty> : (
        <div className="space-y-3" data-testid="clases-list">
          {clases.map((c) => {
            const dada = c.fecha && c.fecha < t;
            return (
              <div key={c.id} className={`rounded-2xl bg-white ring-1 p-4 flex items-center justify-between gap-3 ${dada ? "ring-[#C0392B]/25" : "ring-[#22A860]/25"}`} data-testid={`clase-${c.id}`}>
                <div className="min-w-0">
                  <p className="font-extrabold text-[#0A1C30]">{c.materia_nombre || c.titulo}{c.grupo ? <span className="ml-1.5 text-xs text-[#6B7688]">· {c.grupo}</span> : null}</p>
                  <p className="text-xs text-[#6B7688] mt-0.5 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {c.fecha_local || c.fecha} · {c.hora_local || c.hora}</p>
                </div>
                {dada
                  ? <span className="inline-flex items-center gap-1 text-xs font-extrabold text-[#C0392B] bg-[#FBECEC] rounded-full px-3 py-1.5 flex-shrink-0"><CheckCircle2 className="w-4 h-4" /> Ya la diste</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-extrabold text-[#1E7D48] bg-[#E2F6EB] rounded-full px-3 py-1.5 flex-shrink-0"><CalendarDays className="w-4 h-4" /> Próxima</span>}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

/* ---------------- Conectarme (Supera Connect: sesiones del instructor) ---------------- */
function ConectarView() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => connectApi.list()
    .then((list) => setSessions((list || []).filter((s) => s.estado !== "finalizada")))
    .catch((e) => { toast.error(apiError(e)); setSessions([]); });
  useEffect(() => { load(); }, []);

  const iniciar = async (s) => {
    setBusy(s.id);
    try {
      if (s.estado !== "en_vivo") await connectApi.start(s.id);
      navigate(`/connect/room/${s.id}`);
    } catch (e) { toast.error(apiError(e)); setBusy(""); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  return (
    <Section title="Conectarme a mi Clase" color="#2F5BEA" icon={Radio}>
      <p className="text-sm text-[#6B7688] mb-4">Tus sesiones de Supera Connect asignadas. Inicia la transmisión en vivo y se avisará a tus alumnos automáticamente.</p>
      {sessions === null ? <Empty>Cargando…</Empty> : sessions.length === 0 ? (
        <Empty>Aún no tienes sesiones asignadas. El super admin las cargará desde el panel de Supera Connect.</Empty>
      ) : (
        <div className="space-y-3" data-testid="conectar-list">
          {sessions.map((s) => (
            <div key={s.id} className="rounded-2xl bg-white ring-1 ring-black/5 p-4 flex items-center justify-between gap-3" data-testid={`conectar-item-${s.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-extrabold text-[#0A1C30] truncate">{s.titulo}</p>
                  {s.estado === "en_vivo" && <span className="text-[10px] font-extrabold bg-[#C0140F] text-white rounded-full px-2 py-0.5 animate-pulse">EN VIVO</span>}
                </div>
                {s.scheduled_at && <p className="text-xs text-[#6B7688] mt-0.5 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {fmt(s.scheduled_at)}</p>}
              </div>
              <button onClick={() => iniciar(s)} disabled={busy === s.id} data-testid={`conectar-start-${s.id}`} className="inline-flex items-center gap-1.5 rounded-xl bg-[#C0140F] text-white font-bold px-4 py-2.5 text-sm flex-shrink-0 disabled:opacity-60 hover:bg-[#a01109] transition-colors">
                {busy === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} {s.estado === "en_vivo" ? "Entrar" : "Iniciar transmisión"}
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ---------------- Grabadas ---------------- */
function GrabadasView() {
  const [items, setItems] = useState(null);
  useEffect(() => { instructorApi.grabaciones().then(setItems).catch((e) => { toast.error(apiError(e)); setItems([]); }); }, []);
  return (
    <Section title="Mis Clases Grabadas" color="#8E44AD" icon={Video}>
      {!items ? <Empty>Cargando...</Empty> : items.length === 0 ? <Empty>Aún no hay grabaciones asignadas a tu cuenta.</Empty> : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="grabadas-list">
          {items.map((g) => (
            <a key={g.id} href={g.url} target="_blank" rel="noreferrer" className="rounded-2xl bg-white ring-1 ring-black/5 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2"><Video className="w-5 h-5 text-[#8E44AD]" /><p className="font-extrabold text-[#0A1C30] truncate">{g.titulo}</p></div>
              {g.descripcion && <p className="text-xs text-[#6B7688] mt-1 line-clamp-2">{g.descripcion}</p>}
              <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#2F5BEA]">Ver grabación <ExternalLink className="w-3 h-3" /></span>
            </a>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ---------------- Mi Calificación ---------------- */
function CalificacionView() {
  const [d, setD] = useState(null);
  useEffect(() => { instructorApi.miEvaluacion().then(setD).catch(() => setD({ compartido: false })); }, []);
  return (
    <Section title="Mi Calificación" color="#22A860" icon={Star}>
      <div className="rounded-2xl bg-[#EAF7EF] ring-1 ring-[#22A860]/25 p-4 mb-4">
        <p className="text-sm font-semibold text-[#1E7D48]">En este espacio aparece la encuesta y los comentarios que dan los candidatos y candidatas al instructor Supera.</p>
      </div>
      {!d ? <Empty>Cargando...</Empty> : !d.compartido ? (
        <Empty>Tu evaluación aún no ha sido compartida por la coordinación. Cuando la habiliten, aquí verás tus resultados.</Empty>
      ) : d.sin_datos ? <Empty>Aún no tienes evaluaciones de tus alumnos.</Empty> : (
        <div className="grid md:grid-cols-3 gap-4" data-testid="mi-calificacion">
          <div className="bg-gradient-to-br from-[#0A1C30] to-[#1c3e5f] rounded-2xl p-5 text-white">
            <p className="text-xs font-bold text-white/60">PROMEDIO GENERAL</p>
            <p className="text-4xl font-extrabold mt-1">{d.yo.promedio?.toFixed(2)}<span className="text-lg text-white/50">/5</span></p>
            <div className="flex gap-0.5 mt-1">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= Math.round(d.yo.promedio) ? "fill-[var(--gold)] text-[var(--gold)]" : "text-white/25"}`} />)}</div>
            <p className="text-sm text-white/70 mt-3 flex items-center gap-1.5"><Trophy className="w-4 h-4 text-[var(--gold)]" /> {d.yo.evaluaciones} evaluación(es)</p>
          </div>
          <div className="md:col-span-2 bg-white rounded-2xl ring-1 ring-black/5 p-4">
            <p className="text-xs font-bold text-[#6B7688] mb-2">Promedio por semana</p>
            <div style={{ width: "100%", height: 170 }}>
              <ResponsiveContainer>
                <BarChart data={d.yo.semanas.map((s) => ({ name: s.semana.slice(5), promedio: s.promedio }))} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDF0F4" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6B7688" }} /><YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: "#6B7688" }} />
                  <Tooltip formatter={(v) => [`${v} / 5`, "Promedio"]} /><Bar dataKey="promedio" fill="#22A860" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {d.yo.preguntas.length > 0 && (
            <div className="md:col-span-3 bg-white rounded-2xl ring-1 ring-black/5 p-4">
              <p className="text-xs font-bold text-[#6B7688] mb-2">Promedio por aspecto</p>
              <div className="grid sm:grid-cols-2 gap-2">{d.yo.preguntas.map((q) => <div key={q.id} className="flex items-center justify-between rounded-lg bg-[#F4F6F9] px-3 py-2"><span className="text-sm text-[#2A3446]">{q.label}</span><span className="font-extrabold text-[#0A1C30] text-sm">{q.promedio.toFixed(2)}</span></div>)}</div>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

/* ---------------- Ranking ---------------- */
function RankingView() {
  const [d, setD] = useState(null);
  useEffect(() => { instructorApi.miEvaluacion().then(setD).catch(() => setD({ compartido: false })); }, []);
  return (
    <Section title="Ranking General" color="#E9A400" icon={Trophy}>
      {!d ? <Empty>Cargando...</Empty> : !d.compartido ? <Empty>El ranking se mostrará cuando la coordinación lo comparta contigo.</Empty> : d.sin_datos || !d.ranking?.length ? <Empty>Aún no hay suficientes evaluaciones para generar el ranking.</Empty> : (
        <div data-testid="ranking-list">
          <div className="rounded-2xl bg-gradient-to-br from-[#0A1C30] to-[#1c3e5f] p-5 text-white mb-4">
            <p className="text-xs font-bold text-white/60">TU LUGAR</p>
            <p className="text-4xl font-extrabold mt-1">#{d.yo.rank}<span className="text-lg text-white/50"> de {d.total_evaluados}</span></p>
          </div>
          <div className="bg-white rounded-2xl ring-1 ring-black/5 divide-y divide-black/5">
            {d.ranking.map((r, i) => (
              <div key={i} className={`flex items-center justify-between px-4 py-3 ${r.es_yo ? "bg-[#FFF7E6]" : ""}`}>
                <span className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-[#F4F6F9] grid place-items-center text-xs font-extrabold text-[#0A1C30]">{r.rank}</span><span className={`text-sm ${r.es_yo ? "font-extrabold text-[#0A1C30]" : "text-[#2A3446]"}`}>{r.es_yo ? "Tú" : r.nombre}</span></span>
                <span className="font-extrabold text-[#0A1C30] text-sm">{r.promedio?.toFixed(2)} / 5</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

/* ---------------- Evaluación a candidatos (encuestas) ---------------- */
function EvaluacionView() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [resp, setResp] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setData(await instructorApi.encuestas()); } catch (e) { toast.error(apiError(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const start = (s) => { setOpen(s.semana); setResp(s.respuestas || {}); };
  const submit = async () => { setBusy(true); try { const r = await instructorApi.submitEncuesta({ semana: open, respuestas: resp }); toast.success(r.message || "Enviada."); setOpen(null); await load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  if (!data) return <Section title="Evaluación a Candidatos" color="#7B3FBF" icon={ClipboardList}><Empty>Cargando...</Empty></Section>;
  return (
    <Section title="Evaluación a Candidatos" color="#7B3FBF" icon={ClipboardList}>
      <p className="text-sm text-[#6B7688] mb-3">Completa la evaluación de cada semana. <b>Tus estudiantes no podrán avanzar</b> a la semana siguiente hasta que la envíes.</p>
      <div className="space-y-3" data-testid="evaluacion-list">
        {data.semanas.length === 0 && <Empty>No tienes semanas con clases programadas todavía.</Empty>}
        {data.semanas.map((s) => (
          <div key={s.semana} className="bg-white rounded-2xl ring-1 ring-black/5 p-4" data-testid={`semana-${s.semana}`}>
            <div className="flex items-center justify-between gap-3">
              <div><p className="font-bold text-[#0A1C30] text-sm">Semana del {fmtSemana(s.semana)}</p>
                <span className={`mt-1 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${s.enviada ? "bg-[#E2F6EB] text-[#22A860]" : "bg-[#FBECEC] text-[#C24141]"}`}>{s.enviada ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />} {s.enviada ? "Enviada" : "Pendiente"}</span></div>
              <button data-testid={`encuesta-btn-${s.semana}`} onClick={() => start(s)} className={`rounded-xl font-bold px-3 py-2 text-xs ${s.enviada ? "bg-[#F4F6F9] text-[#0A1C30]" : "bg-[#7B3FBF] text-white"}`}>{s.enviada ? "Revisar / editar" : "Responder"}</button>
            </div>
            {open === s.semana && (
              <div className="mt-4 space-y-3 border-t border-black/5 pt-4">
                {data.preguntas.map((q) => (
                  <div key={q.id}><label className="block text-xs font-bold text-[#0A1C30] mb-1">{q.label}</label>
                    {q.tipo === "escala"
                      ? <select data-testid={`q-${q.id}`} value={resp[q.id] || ""} onChange={(e) => setResp((p) => ({ ...p, [q.id]: e.target.value }))} className="w-full rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm bg-white"><option value="">—</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select>
                      : <textarea data-testid={`q-${q.id}`} value={resp[q.id] || ""} onChange={(e) => setResp((p) => ({ ...p, [q.id]: e.target.value }))} rows={2} className="w-full rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm" />}
                  </div>
                ))}
                <button data-testid={`encuesta-submit-${s.semana}`} onClick={submit} disabled={busy} className="rounded-xl bg-[#7B3FBF] text-white font-extrabold px-6 py-2.5 disabled:opacity-60 inline-flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Enviar</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- Materiales ---------------- */
function MaterialesView() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [link, setLink] = useState({ titulo: "", url: "", tipo: "video" });
  const fileRef = useRef(null);
  const load = () => instructorApi.materiales().then(setItems).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  const doUpload = async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy(true); try { await instructorApi.uploadMaterialGeneral(f.name, f); toast.success("Material subido."); load(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; } };
  const addLink = async () => { if (!link.titulo.trim() || !link.url.trim()) { toast.error("Título y enlace obligatorios."); return; } setBusy(true); try { await instructorApi.linkMaterialGeneral(link); toast.success("Enlace agregado."); setLink({ titulo: "", url: "", tipo: "video" }); setShowLink(false); load(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };
  const del = async (id) => { try { await instructorApi.deleteMaterialGeneral(id); toast.success("Eliminado."); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Section title="Materiales" color="#0A9BAF" icon={FolderOpen}>
      <p className="text-sm text-[#6B7688] mb-3">Sube documentos, imágenes, audio y enlaces (videos) que uses o desees compartir para tus clases.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <button data-testid="mat-upload" onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 rounded-xl bg-navy text-white font-bold px-4 py-2.5 text-sm disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir archivo</button>
        <button data-testid="mat-link" onClick={() => setShowLink((v) => !v)} className="inline-flex items-center gap-1.5 rounded-xl bg-[#EDF2FF] text-[#2F5BEA] font-bold px-4 py-2.5 text-sm"><Link2 className="w-4 h-4" /> Agregar enlace</button>
        <input ref={fileRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.ppt,.pptx,.key,.mp3,.wav,.m4a,.ogg,.aac,.doc,.docx,.txt" onChange={doUpload} />
      </div>
      {showLink && (
        <div className="grid sm:grid-cols-3 gap-2 mb-4" data-testid="mat-link-form">
          <input value={link.titulo} onChange={(e) => setLink((p) => ({ ...p, titulo: e.target.value }))} placeholder="Título" className="rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm" />
          <input value={link.url} onChange={(e) => setLink((p) => ({ ...p, url: e.target.value }))} placeholder="https://..." className="rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm" />
          <button onClick={addLink} disabled={busy} className="rounded-lg bg-[var(--gold)] text-[#0A1C30] font-bold px-3 py-2 text-sm disabled:opacity-60">Guardar</button>
        </div>
      )}
      {!items ? <Empty>Cargando...</Empty> : items.length === 0 ? <Empty>Aún no has subido materiales.</Empty> : (
        <div className="space-y-1.5" data-testid="materiales-list">
          {items.map((m) => { const Icon = TIPO_ICON[m.tipo] || Paperclip; return (
            <div key={m.id} className="flex items-center justify-between gap-2 rounded-lg bg-white ring-1 ring-black/5 px-3 py-2.5">
              <a href={mediaUrl(m.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0 text-sm font-semibold text-[#0A1C30] hover:underline"><Icon className="w-4 h-4 text-[#0A9BAF] flex-shrink-0" /><span className="truncate">{m.titulo}</span><span className="text-[10px] uppercase text-[#9AA3B2] font-bold">{m.tipo}</span></a>
              <button data-testid={`del-mat-${m.id}`} onClick={() => del(m.id)} className="text-[#C24141] hover:text-[#8f2d2d]"><Trash2 className="w-4 h-4" /></button>
            </div>
          ); })}
        </div>
      )}
    </Section>
  );
}

/* ---------------- Contratación ---------------- */
function ContratacionView() {
  const [items, setItems] = useState(null);
  useEffect(() => { instructorApi.contratacion().then(setItems).catch((e) => { toast.error(apiError(e)); setItems([]); }); }, []);
  return (
    <Section title="Contratación" color="#2A3446" icon={FileSignature}>
      <p className="text-sm text-[#6B7688] mb-3">Aquí encontrarás tu convenio de contratación y acuerdo de colaboración con Supera.</p>
      {!items ? <Empty>Cargando...</Empty> : items.length === 0 ? <Empty>Aún no hay documentos de contratación disponibles.</Empty> : (
        <div className="space-y-3" data-testid="contratacion-list">
          {items.map((c) => (
            <div key={c.id} className="rounded-2xl bg-white ring-1 ring-black/5 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0"><p className="font-extrabold text-[#0A1C30]">{c.titulo}</p>{c.nota && <p className="text-xs text-[#6B7688] mt-0.5">{c.nota}</p>}</div>
                {c.url && <a href={mediaUrl(c.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-xl bg-navy text-white font-bold px-4 py-2.5 text-sm flex-shrink-0"><FileText className="w-4 h-4" /> Ver</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ---------------- Pagos ---------------- */
function PagosView() {
  const [d, setD] = useState(null);
  useEffect(() => { instructorApi.pagos().then(setD).catch((e) => { toast.error(apiError(e)); }); }, []);
  if (!d) return <Section title="Mis Pagos Recibidos" color="#22A860" icon={Wallet}><Empty>Cargando...</Empty></Section>;
  const t = d.totales;
  const kpis = [
    ["Clases impartidas", t.clases_impartidas, "#0A1C30"],
    ["Clases pendientes", t.clases_pendientes, "#E9A400"],
    ["Pagado", `$${t.pagado_usd.toLocaleString()} USD / $${t.pagado_mxn.toLocaleString()} MXN`, "#22A860"],
    ["Por pagar", `$${t.pendiente_usd.toLocaleString()} USD / $${t.pendiente_mxn.toLocaleString()} MXN`, "#C0392B"],
  ];
  return (
    <Section title="Mis Pagos Recibidos" color="#22A860" icon={Wallet}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="pagos-kpis">
        {kpis.map(([l, v, c]) => <div key={l} className="bg-white rounded-2xl ring-1 ring-black/5 p-4"><p className="text-xs font-bold text-[#6B7688]">{l}</p><p className="mt-1 font-extrabold" style={{ color: c }}>{v}</p></div>)}
      </div>
      <h3 className="font-extrabold text-[#0A1C30] text-sm mb-2">Detalle de pagos</h3>
      {d.pagos.length === 0 ? <Empty>Aún no hay pagos registrados por Supera.</Empty> : (
        <div className="bg-white rounded-2xl ring-1 ring-black/5 overflow-x-auto" data-testid="pagos-list">
          <table className="w-full text-sm"><thead className="bg-[#F4F6F9] text-[#6B7688]"><tr><th className="text-left px-4 py-3 font-bold">Concepto / Clase</th><th className="text-left px-4 py-3 font-bold">Fecha clase</th><th className="text-right px-4 py-3 font-bold">Monto</th><th className="px-4 py-3 font-bold">Estado</th><th className="text-left px-4 py-3 font-bold">Pago</th></tr></thead>
            <tbody className="divide-y divide-black/5">
              {d.pagos.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-3 font-bold text-[#0A1C30]">{p.concepto || "Clase"}</td>
                  <td className="px-4 py-3 text-[#2A3446]">{p.fecha_clase || "—"}</td>
                  <td className="px-4 py-3 text-right font-bold">${Number(p.monto || 0).toLocaleString()} {p.moneda}</td>
                  <td className="px-4 py-3 text-center"><span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold text-white ${p.estado === "realizado" ? "bg-[#22A860]" : "bg-[#E9A400]"}`}>{p.estado === "realizado" ? "Realizado" : "Pendiente"}</span></td>
                  <td className="px-4 py-3 text-[#6B7688] text-xs">{p.fecha_pago || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/* ---------------- Preguntas y comentarios ---------------- */
function PreguntasView() {
  const [msgs, setMsgs] = useState(null);
  const [reply, setReply] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => instructorApi.mensajes().then(setMsgs).catch((e) => { toast.error(apiError(e)); setMsgs([]); });
  useEffect(() => { load(); }, []);
  // group by user_id
  const threads = {};
  (msgs || []).forEach((m) => { (threads[m.user_id] = threads[m.user_id] || { nombre: m.user_nombre, items: [] }).items.push(m); });
  const send = async (parentId) => { const texto = (reply[parentId] || "").trim(); if (!texto) return; setBusy(true); try { const r = await instructorApi.responderMensaje({ parent_id: parentId, texto }); toast.success(r.message || "Enviado."); setReply((s) => ({ ...s, [parentId]: "" })); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  return (
    <Section title="Preguntas y Comentarios" color="#C0392B" icon={MessagesSquare}>
      <div className="rounded-2xl bg-[#FDECEC] ring-1 ring-[#C0392B]/20 p-4 mb-4">
        <p className="text-sm font-semibold text-[#8A4B45]">Este espacio está dedicado a las preguntas o comentarios de la clase que los candidatos y candidatas tienen hacia el instructor. Las preguntas son revisadas por la coordinación antes de llegar a ti.</p>
      </div>
      {!msgs ? <Empty>Cargando...</Empty> : Object.keys(threads).length === 0 ? <Empty>Aún no tienes preguntas o comentarios autorizados.</Empty> : (
        <div className="space-y-4" data-testid="preguntas-list">
          {Object.entries(threads).map(([uid, th]) => (
            <div key={uid} className="bg-white rounded-2xl ring-1 ring-black/5 p-4">
              <p className="font-extrabold text-[#0A1C30] text-sm flex items-center gap-1.5"><Users className="w-4 h-4 text-[#C0392B]" /> {th.nombre || "Candidato/a"}</p>
              <div className="mt-3 space-y-2">
                {th.items.map((m) => (
                  <div key={m.id} className={`rounded-xl px-3 py-2 text-sm ${m.autor === "instructor" ? "bg-[#EDF2FF] ml-6" : "bg-[#F4F6F9] mr-6"}`}>
                    <p className="text-[10px] font-bold uppercase text-[#9AA3B2] mb-0.5">{m.autor === "instructor" ? "Tú" : "Candidato/a"}{m.autor === "instructor" && m.estado !== "autorizado" ? " · pendiente de aprobación" : ""}</p>
                    <p className="text-[#2A3446]">{m.texto}</p>
                    {m.autor === "candidato" && (
                      <div className="mt-2 flex gap-2">
                        <input data-testid={`reply-input-${m.id}`} value={reply[m.id] || ""} onChange={(e) => setReply((s) => ({ ...s, [m.id]: e.target.value }))} placeholder="Responder (opcional)..." className="flex-1 rounded-lg ring-1 ring-black/10 px-3 py-1.5 text-sm" />
                        <button data-testid={`reply-send-${m.id}`} onClick={() => send(m.id)} disabled={busy} className="rounded-lg bg-[#C0392B] text-white font-bold px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-60"><Send className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ---------------- Chat ---------------- */
function ChatView({ navigate }) {
  return (
    <Section title="Chat" color="#2F5BEA" icon={MessageSquare}>
      <p className="text-sm text-[#6B7688] mb-4">Elige con quién quieres conversar.</p>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="chat-options">
        <button data-testid="chat-staff" onClick={() => navigate("/chat")} className="rounded-2xl bg-white ring-1 ring-black/5 p-5 text-left hover:shadow-md transition-shadow">
          <span className="w-11 h-11 rounded-xl bg-[#EDF2FF] grid place-items-center"><MessageSquare className="w-6 h-6 text-[#2F5BEA]" /></span>
          <p className="mt-3 font-extrabold text-[#0A1C30]">Staff Supera</p><p className="text-xs text-[#6B7688]">Habla con el equipo de Supera.</p>
        </button>
        <button data-testid="chat-assistant" onClick={() => navigate("/asistente")} className="rounded-2xl bg-white ring-1 ring-black/5 p-5 text-left hover:shadow-md transition-shadow">
          <span className="w-11 h-11 rounded-xl bg-[#FFF3D6] grid place-items-center"><Bot className="w-6 h-6 text-[#E9A400]" /></span>
          <p className="mt-3 font-extrabold text-[#0A1C30]">Asistente Virtual</p><p className="text-xs text-[#6B7688]">Resuelve dudas al instante con la IA.</p>
        </button>
      </div>
    </Section>
  );
}

export default function InstructorPortal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState(null);

  const load = useCallback(async () => { try { setData(await instructorApi.me()); } catch (e) { setErr(apiError(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const doLogout = () => { logout(); navigate("/login"); };

  if (err) return (
    <div className="min-h-screen bg-cream grid place-items-center px-6"><div className="text-center"><p className="text-[#C24141] font-bold" data-testid="instructor-error">{err}</p><button onClick={doLogout} className="mt-4 rounded-xl bg-navy text-white font-bold px-5 py-2.5">Cerrar sesión</button></div></div>
  );
  if (!data) return <div className="min-h-screen bg-cream grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[#E9A400]" /></div>;

  const p = data.perfil;
  const renderView = () => {
    switch (view) {
      case "clases": return <ClasesView clases={data.clases} />;
      case "conectar": return <ConectarView />;
      case "grabadas": return <GrabadasView />;
      case "calificacion": return <CalificacionView />;
      case "evaluacion": return <EvaluacionView />;
      case "ranking": return <RankingView />;
      case "materiales": return <MaterialesView />;
      case "contratacion": return <ContratacionView />;
      case "pagos": return <PagosView />;
      case "preguntas": return <PreguntasView />;
      case "chat": return <ChatView navigate={navigate} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-16" data-testid="instructor-portal">
      <header className="bg-navy sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <img src="/logo-supera-light.png" alt="Supera" className="h-11 w-auto object-contain" />
          <button data-testid="instructor-logout" onClick={doLogout} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold"><LogOut className="w-4 h-4" /> Salir</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="rounded-3xl bg-gradient-to-br from-[#0A1C30] to-[#1c3e5f] p-6 md:p-8 text-white relative overflow-hidden flex items-center gap-4">
          <GraduationCap className="absolute -right-4 -top-4 w-28 h-28 text-white/5" />
          {p.foto_url ? <img src={mediaUrl(p.foto_url)} alt={p.nombre} className="w-16 h-16 rounded-2xl object-cover ring-2 ring-[var(--gold)]" /> : <div className="w-16 h-16 rounded-2xl bg-[var(--gold)] grid place-items-center text-[#0A1C30] font-extrabold text-xl">{(p.nombre || "?")[0]}</div>}
          <div><p className="text-xs font-extrabold tracking-[0.25em] text-[var(--gold)]">PORTAL DEL INSTRUCTOR</p><h1 className="text-2xl font-extrabold mt-0.5">{p.nombre} {p.apellidos}</h1>{p.especialidad && <p className="text-sm text-white/70">{p.especialidad}</p>}</div>
        </div>

        {!view ? (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="instructor-menu">
            {MENU.map(([key, label, Icon, color]) => (
              <button key={key} data-testid={`menu-${key}`} onClick={() => key === "superaconnect" ? navigate("/connect") : setView(key)} className="group rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-4 sm:p-5 text-left transition-all hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]">
                <span className="w-12 h-12 rounded-xl grid place-items-center" style={{ backgroundColor: `${color}1A` }}><Icon className="w-6 h-6" style={{ color }} /></span>
                <p className="mt-3 font-extrabold text-[#0A1C30] text-sm leading-tight">{label}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-6">
            <button data-testid="view-back" onClick={() => setView(null)} className="mb-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#6B7688] hover:text-[#0A1C30]"><ArrowLeft className="w-4 h-4" /> Volver al panel</button>
            {renderView()}
          </div>
        )}
      </div>
    </div>
  );
}
